<template>
  <div
    class="ai-menu-v2__recent-item"
    :class="{
      active,
      'is-mobile': mobile,
      'is-menu-open': menuOpen
    }"
    @click.capture="guardLongPressClick"
    @contextmenu="handleContextMenu"
    @touchcancel="cancelLongPress"
    @touchend="endLongPress"
    @touchmove.passive="moveLongPress"
    @touchstart.passive="startLongPress"
  >
    <button class="ai-menu-v2__session-select" type="button" @click="emits('select', session)">
      <i
        v-if="session.sourceType === 'schedule'"
        aria-hidden="true"
        class="icon-timer-line t-iconfont"
      ></i>
      <span v-marquee class="ai-menu-v2__text">
        <span class="ai-menu-v2__text-track">
          <span class="ai-menu-v2__text-copy">{{ session.title || '未知标题会话' }}</span>
          <span aria-hidden="true" class="ai-menu-v2__text-copy">{{
            session.title || '未知标题会话'
          }}</span>
        </span>
      </span>
    </button>
    <div class="right-box" :class="{ 'has-status': session.isRunning || !session.isRead }">
      <img v-if="session.isRunning" alt="运行中" class="ai-menu-v2__running" :src="runningIcon" />
      <span v-else-if="!session.isRead" class="ai-menu-v2__unread"></span>
      <el-dropdown
        v-if="!mobile"
        class="ai-menu-v2__more"
        placement="bottom-start"
        popper-class="ai-session-actions"
        :show-arrow="false"
        trigger="click"
        @command="emits('command', $event, session)"
        @visible-change="menuOpen = $event"
      >
        <button
          aria-label="更多操作"
          class="ai-menu-v2__more-button"
          :disabled="busy"
          type="button"
          @click.stop
        >
          <i aria-hidden="true" class="icon-more-line t-iconfont"></i>
        </button>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="top" :disabled="busy">
              <i
                aria-hidden="true"
                class="t-iconfont"
                :class="isTop ? 'icon-unpin-line' : 'icon-pushpin-line'"
              ></i
              >{{ isTop ? '取消置顶' : '置顶' }}
            </el-dropdown-item>
            <el-dropdown-item command="rename" :disabled="busy"
              ><i aria-hidden="true" class="icon-edit-line t-iconfont"></i>重命名</el-dropdown-item
            >
            <el-dropdown-item
              class="ai-session-actions__delete"
              command="delete"
              :disabled="busy"
              divided
              ><i aria-hidden="true" class="icon-delete-bin-line t-iconfont"></i
              >删除</el-dropdown-item
            >
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
  </div>
</template>

<script setup>
import { ref, onBeforeUnmount } from 'vue'
import runningIcon from '@/assets/session-loading.svg'

const props = defineProps({
  session: { type: Object, required: true },
  mobile: Boolean,
  isTop: { type: Number, default: 0 },
  active: Boolean,
  busy: Boolean,
  longPressDisabled: Boolean
})
const emits = defineEmits(['select', 'command', 'longpress'])
const menuOpen = ref(false)
let longPressTimer = null
let longPressOrigin = null
let longPressActivated = false
let suppressClickUntil = 0
let isUnmounted = false

const marqueeStates = new WeakMap()
const vMarquee = {
  mounted(el) {
    const track = el.firstElementChild
    const text = track.firstElementChild
    const button = el.closest('.ai-menu-v2__recent-item')
    let animation = null
    let hovered = false
    let distance = 0

    const stop = () => {
      animation?.cancel()
      animation = null
    }
    const play = () => {
      stop()
      if (!hovered || !distance || props.mobile) return
      animation = track.animate(
        [{ transform: 'translateX(0)' }, { transform: `translateX(-${distance}px)` }],
        { duration: (distance / 36) * 1000, iterations: Infinity, easing: 'linear' }
      )
    }
    const measure = () => {
      // 使用正文宽度判断溢出，不把循环副本和首尾间距计入。
      const textWidth =
        text.getBoundingClientRect().width - parseFloat(getComputedStyle(text).paddingRight)
      const overflowing = textWidth > el.clientWidth
      el.classList.toggle('is-overflowing', overflowing)
      const nextDistance = overflowing ? text.getBoundingClientRect().width : 0
      if (distance !== nextDistance) {
        distance = nextDistance
        play()
      }
    }
    const enter = () => {
      hovered = true
      measure()
      play()
    }
    const leave = () => {
      hovered = false
      stop()
    }
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    observer.observe(text)
    button.addEventListener('mouseenter', enter)
    button.addEventListener('mouseleave', leave)
    marqueeStates.set(el, {
      update: () => {
        measure()
        play()
      },
      destroy: () => {
        stop()
        observer.disconnect()
        button.removeEventListener('mouseenter', enter)
        button.removeEventListener('mouseleave', leave)
      }
    })
    measure()
  },
  updated(el) {
    marqueeStates.get(el)?.update()
  },
  beforeUnmount(el) {
    marqueeStates.get(el)?.destroy()
    marqueeStates.delete(el)
  }
}

const cancelLongPress = () => {
  clearTimeout(longPressTimer)
  longPressTimer = null
  longPressOrigin = null
}

const startLongPress = (event) => {
  cancelLongPress()
  longPressActivated = false
  if (!props.mobile || props.busy || props.longPressDisabled || event.touches.length !== 1) return
  const touch = event.touches[0]
  longPressOrigin = { x: touch.clientX, y: touch.clientY }
  const row = event.currentTarget
  longPressTimer = setTimeout(() => {
    cancelLongPress()
    if (isUnmounted) return
    longPressActivated = true
    suppressClickUntil = Date.now() + 800
    const rect = row.getBoundingClientRect()
    emits('longpress', props.session, { top: rect.top, left: rect.left, width: rect.width })
  }, 500)
}

const moveLongPress = (event) => {
  if (!longPressOrigin) return
  const touch = event.touches[0]
  if (
    event.touches.length !== 1 ||
    Math.hypot(touch.clientX - longPressOrigin.x, touch.clientY - longPressOrigin.y) > 10
  )
    cancelLongPress()
}

const endLongPress = (event) => {
  cancelLongPress()
  if (longPressActivated) {
    suppressClickUntil = Date.now() + 800
    if (event.cancelable) event.preventDefault()
    longPressActivated = false
  }
}

const guardLongPressClick = (event) => {
  if (!props.mobile || Date.now() >= suppressClickUntil) return
  event.preventDefault()
  event.stopPropagation()
}

const handleContextMenu = (event) => {
  if (props.mobile) event.preventDefault()
}

onBeforeUnmount(() => {
  isUnmounted = true
  cancelLongPress()
})
</script>

<style lang="scss" scoped>
.ai-menu-v2__recent-item {
  width: 100%;
  text-align: left;
  display: flex;
  align-items: center;
  padding: 4px 4px 4px 12px;
  border-radius: 12px;
  min-height: 32px;
  box-sizing: border-box;
  i {
    flex-shrink: 0;
    margin-right: 8px;
    font-size: 16px;
    line-height: 16px;
    color: #646d7d;
  }
}
.ai-menu-v2__recent-item + .ai-menu-v2__recent-item {
  margin-top: 2px;
}
.right-box {
  position: relative;
  flex-shrink: 0;
  width: 0;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 0;

  &.has-status {
    width: 24px;
    margin-left: 8px;
  }
}
.ai-menu-v2__running {
  display: block;
  width: 20px;
  height: 20px;
  animation: recent-conversation-running 1s linear infinite;
}
@keyframes recent-conversation-running {
  to {
    transform: rotate(360deg);
  }
}
.ai-menu-v2__unread {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #7a52f4;
}

.ai-menu-v2__recent-item {
  &.is-mobile {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
  }
  .ai-menu-v2__session-select {
    display: flex;
    align-items: center;
    min-width: 0;
    flex: 1;
    padding: 0;
    text-align: left;
  }
  .ai-menu-v2__more {
    // 隐藏时保留定位尺寸，避免下拉菜单退场期间锚点变成 (0, 0)。
    position: absolute;
    top: 0;
    right: 0;
    display: inline-flex;
    width: 24px;
    height: 24px;
    visibility: hidden;
    pointer-events: none;
  }
  .ai-menu-v2__more-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border-radius: 8px;
    background: #e1e1e1;
    &:hover {
      background: #eeeeee;
    }
    .t-iconfont {
      margin: 0;
      font-size: 16px;
    }
  }
  &.is-menu-open {
    background: #e1e1e1;
    .ai-menu-v2__text::after {
      background: linear-gradient(to right, rgba(225, 225, 225, 0), #e1e1e1 80%) !important;
    }
  }
  &.is-menu-open,
  &:not(.is-mobile):focus-within {
    .right-box {
      width: 24px;
      margin-left: 8px;
    }
    .ai-menu-v2__more {
      visibility: visible;
      pointer-events: auto;
    }
    .ai-menu-v2__unread {
      display: none;
    }
  }
  @media (hover: hover) and (pointer: fine) {
    &:not(.is-mobile):hover {
      .right-box {
        width: 24px;
        margin-left: 8px;
      }
      .ai-menu-v2__more {
        visibility: visible;
        pointer-events: auto;
      }
      .ai-menu-v2__unread {
        display: none;
      }
    }
  }
  button {
    font: inherit;
    color: inherit;
    cursor: pointer;
    border: 0;
    background: transparent;
  }
  button:focus-visible {
    outline: 2px solid #7a52f4;
    outline-offset: -2px;
  }
  .ai-menu-v2__text {
    position: relative;
    display: block;
    min-width: 0;
    flex: 1;
    overflow: hidden;
    white-space: nowrap;
    &.is-overflowing::after {
      content: '';
      position: absolute;
      top: 50%;
      right: 0;
      width: 32px;
      height: 20px;
      transform: translateY(-50%);
      pointer-events: none;
      background: linear-gradient(to right, rgba(238, 238, 238, 0), #eeeeee 80%);
    }
  }
  .ai-menu-v2__text-track {
    display: inline-flex;
    vertical-align: top;
  }
  .ai-menu-v2__text-copy {
    flex-shrink: 0;
    &[aria-hidden='true'] {
      display: none;
    }
  }
  .is-overflowing .ai-menu-v2__text-copy {
    padding-right: 2em;
    &[aria-hidden='true'] {
      display: block;
    }
  }
  &.active {
    background: #e1e1e1;
    .ai-menu-v2__text::after {
      background: linear-gradient(to right, rgba(225, 225, 225, 0), #e1e1e1 80%);
    }
  }
  @media (hover: hover) and (pointer: fine) {
    &:not(.is-mobile):hover {
      background: #e1e1e1;
      .ai-menu-v2__text::after {
        background: linear-gradient(to right, rgba(225, 225, 225, 0), #e1e1e1 80%);
      }
    }
  }
}
:global(.ai-session-actions.el-popper) {
  width: 164px;
  padding: 4px;
  box-sizing: border-box;
  border: 1px solid #eef1f8;
  border-radius: 12px;
  background: #ffffff;
  box-shadow:
    0 6px 16px rgba(15, 23, 42, 0.05),
    0 1px 2px rgba(15, 23, 42, 0.04);
}
:global(.ai-session-actions .el-dropdown-menu) {
  padding: 0;
}
:global(.ai-session-actions .el-dropdown-menu__item) {
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 22px;
  color: #242832;
}
:global(.ai-session-actions .el-dropdown-menu__item + .el-dropdown-menu__item) {
  margin-top: 4px;
}
:global(.ai-session-actions .el-dropdown-menu__item i) {
  margin-right: 8px;
  font-size: 16px;
}
:global(.ai-session-actions .el-dropdown-menu__item:not(.is-disabled):hover) {
  background: #f2f3f5;
}
:global(.ai-session-actions .el-dropdown-menu__item--divided) {
  margin-top: 4px;
  border-color: #eef1f8;
}
:global(.ai-session-actions .el-dropdown-menu__item--divided::before) {
  height: 4px;
}
:global(.ai-session-actions .ai-session-actions__delete) {
  color: #b42318;
}
:global(.ai-session-actions .ai-session-actions__delete:not(.is-disabled):hover),
:global(.ai-session-actions .ai-session-actions__delete:not(.is-disabled):focus) {
  color: #b42318;
}
</style>
